<!--
  Admin mode › Flexi › Settings › Safety round templates › one template.
  Stacked cards: metadata, one card per area, the assigned businesses.
-->
<div class="fvr-editor" [attr.aria-busy]="loading || busy">
  <header class="fvr-head fvr-editor__head">
    <div class="fvr-editor__heading">
      <a class="fvr-back" href="javascript:void(0)" (click)="backToLibrary()">
        <i class="pi pi-angle-left" aria-hidden="true"></i>{{ 'FLEXI_SAFETY_ROUNDS.LIBRARY.TITLE' | translate }}
      </a>
      @if (template; as t) {
        <div class="fvr-editor__title-row">
          <h2 class="fvr-title">{{ name || t.Name }}</h2>
          <span class="fvr-pill" [class.fvr-pill--done]="isPublished" [class.fvr-pill--draft]="t.Status === TemplateStatus.Draft">
            {{ 'FLEXI_SAFETY_ROUNDS.TEMPLATE_STATUS.' + t.Status | translate }}
            @if (t.Version > 0) {
              {{ 'FLEXI_SAFETY_ROUNDS.VERSION' | translate: { version: t.Version } }}
            }
          </span>
          @if (isDirty && !isArchived) {
            <span class="fvr-pill fvr-pill--draft">{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.UNSAVED' | translate }}</span>
          }
        </div>
        <p class="fvr-subtitle">
          {{ (isArchived ? 'FLEXI_SAFETY_ROUNDS.EDITOR.ARCHIVED_NOTE' : 'FLEXI_SAFETY_ROUNDS.EDITOR.SNAPSHOT_NOTE') | translate: { version: t.Version } }}
        </p>
      }
    </div>

    @if (template && !isArchived) {
      <div class="fvr-editor__actions">
        <button type="button" class="fas-btn fas-btn--ghost" [disabled]="loading || busy" (click)="confirmArchive()">
          @if (archiving) {
            <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
          }
          {{ (archiving ? 'FLEXI_SAFETY_ROUNDS.FEEDBACK.ARCHIVING' : 'FLEXI_SAFETY_ROUNDS.EDITOR.ARCHIVE') | translate }}
        </button>
        <button type="button" class="fas-btn fas-btn--ghost" [disabled]="loading || busy || !isDirty" (click)="save()">
          @if (saving) {
            <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
          }
          {{ (saving ? 'FLEXI_SAFETY_ROUNDS.SAVING' : 'FLEXI_SAFETY_ROUNDS.EDITOR.SAVE') | translate }}
        </button>
        <button type="button" class="fas-btn fas-btn--primary" [disabled]="loading || busy || !itemCount" (click)="publish()">
          @if (publishing) {
            <i class="pi pi-spin pi-spinner" aria-hidden="true"></i>
          }
          {{ (publishing ? 'FLEXI_SAFETY_ROUNDS.FEEDBACK.PUBLISHING' : 'FLEXI_SAFETY_ROUNDS.EDITOR.PUBLISH') | translate: { version: (template?.Version ?? 0) + 1 } }}
        </button>
      </div>
    }
  </header>

  @if (submitted && invalidKey) {
    <p class="fas-error" role="alert">{{ invalidKey | translate }}</p>
  }

  @if (loading) {
    <flexi-safety-round-skeleton />
  }

  @if (loadFailed) {
    <p class="fas-error" role="alert">{{ 'FLEXI_SAFETY_ROUNDS.LOAD_FAILED' | translate }}</p>
    <button type="button" class="fas-btn fas-btn--ghost" (click)="load()">{{ 'FLEXI_SAFETY_ROUNDS.ASSIGN.RETRY' | translate }}</button>
  }

  @if (busy) {
    <p class="fvr-action-status" role="status">{{ 'FLEXI_SAFETY_ROUNDS.FEEDBACK.WORKING' | translate }}</p>
  }
  <div class="fvr-operation-content" [attr.inert]="busy ? '' : null">
  @if (template && !loading) {
    <!-- ═══════════ About the template ═══════════ -->
    <section class="fvr-card fvr-editor__card" aria-labelledby="fvr-meta-heading">
      <h3 class="fvr-h3" id="fvr-meta-heading">{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.ABOUT' | translate }}</h3>
      <div class="fvr-editor__grid">
        <div class="fvr-field">
          <label for="fvr-ed-name">{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.NAME' | translate }} <span class="fvr-required">*</span></label>
          <input pInputText id="fvr-ed-name" type="text" maxlength="200" [(ngModel)]="name" [disabled]="isArchived" />
        </div>
        <div class="fvr-field">
          <label for="fvr-ed-industry">{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.INDUSTRY' | translate }}</label>
          <input pInputText id="fvr-ed-industry" type="text" maxlength="200" [(ngModel)]="industry" [disabled]="isArchived" />
        </div>
      </div>
      <div class="fvr-field">
        <label for="fvr-ed-description">{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.DESCRIPTION' | translate }}</label>
        <textarea pTextarea id="fvr-ed-description" rows="2" maxlength="2000" [(ngModel)]="description" [disabled]="isArchived"></textarea>
      </div>
      <div class="fvr-editor__default">
        <button
          type="button"
          role="switch"
          class="fvr-switch"
          id="fvr-ed-default"
          [attr.aria-checked]="template.IsDefaultForNewBusinesses"
          [disabled]="!isPublished || togglingDefault"
          (click)="toggleDefault()"
        ></button>
        <label for="fvr-ed-default">
          <strong>{{ 'FLEXI_SAFETY_ROUNDS.LIBRARY.COLUMN_DEFAULT' | translate }}</strong>
          @if (!isPublished) {
            <span class="fas-muted"> · {{ 'FLEXI_SAFETY_ROUNDS.LIBRARY.DEFAULT_NEEDS_PUBLISHED' | translate }}</span>
          }
        </label>
      </div>
      <div class="fvr-editor__due">
        <span class="fvr-hint">{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.DUE_DAYS_INTRO' | translate }}</span>
        <div class="fvr-editor__due-row">
          <div class="fvr-editor__due-field">
            <label for="fvr-due-2"><span class="fvr-grade fvr-grade--2">2</span></label>
            <input pInputText id="fvr-due-2" type="number" min="1" max="3650" [(ngModel)]="dueDays.ShouldImprove" [disabled]="isArchived" />
            <span class="fas-muted">{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.DAYS' | translate }}</span>
          </div>
          <div class="fvr-editor__due-field">
            <label for="fvr-due-3"><span class="fvr-grade fvr-grade--3">3</span></label>
            <input pInputText id="fvr-due-3" type="number" min="1" max="3650" [(ngModel)]="dueDays.LegalBreach" [disabled]="isArchived" />
            <span class="fas-muted">{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.DAYS' | translate }}</span>
          </div>
          <div class="fvr-editor__due-field">
            <label for="fvr-due-4"><span class="fvr-grade fvr-grade--4">4</span></label>
            <input pInputText id="fvr-due-4" type="number" min="1" max="3650" [(ngModel)]="dueDays.Danger" [disabled]="isArchived" />
            <span class="fas-muted">{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.DAYS' | translate }}</span>
          </div>
        </div>
      </div>
    </section>

    <!-- ═══════════ Areas ═══════════ -->
    @for (section of sections; track $index; let a = $index; let first = $first; let last = $last) {
      <section class="fvr-card fvr-editor__card fvr-area" [class.fvr-area--open]="openArea === a">
        <div class="fvr-area__head">
          @if (renamingArea === a) {
            <form class="fvr-area__rename" (ngSubmit)="commitRename()">
              <label class="fas-sr-only" [for]="'fvr-area-name-' + a">{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.AREA_NAME' | translate }}</label>
              <input pInputText [id]="'fvr-area-name-' + a" name="areaName" type="text" maxlength="200" [(ngModel)]="renameValue" />
              <button type="submit" class="fas-btn fas-btn--primary">{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.OK' | translate }}</button>
              <button type="button" class="fas-btn fas-btn--ghost" (click)="cancelRename()">{{ 'CANCEL' | translate }}</button>
            </form>
          } @else {
            <button
              type="button"
              class="fvr-area__toggle"
              [attr.aria-expanded]="openArea === a"
              (click)="toggleArea(a)"
            >
              <i class="pi" [ngClass]="openArea === a ? 'pi-chevron-down' : 'pi-chevron-right'" aria-hidden="true"></i>
              <span class="fvr-area__name">{{ a + 1 }}. {{ section.name }}</span>
              <span class="fas-muted">· {{ 'FLEXI_SAFETY_ROUNDS.EDITOR.AREA_COUNT' | translate: { count: section.items.length } }}</span>
            </button>
          }
          @if (!isArchived && renamingArea !== a) {
            <div class="fvr-area__tools">
              <button type="button" class="fas-icon-btn" [disabled]="first || !!editing" [attr.aria-label]="'FLEXI_SAFETY_ROUNDS.EDITOR.MOVE_AREA_UP' | translate" (click)="moveArea(a, -1)">
                <i class="pi pi-arrow-up" aria-hidden="true"></i>
              </button>
              <button type="button" class="fas-icon-btn" [disabled]="last || !!editing" [attr.aria-label]="'FLEXI_SAFETY_ROUNDS.EDITOR.MOVE_AREA_DOWN' | translate" (click)="moveArea(a, 1)">
                <i class="pi pi-arrow-down" aria-hidden="true"></i>
              </button>
              <button type="button" class="fas-btn fas-btn--text" (click)="startRename(a)">{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.RENAME' | translate }}</button>
              <button type="button" class="fas-icon-btn" [disabled]="!!editing" [attr.aria-label]="'FLEXI_SAFETY_ROUNDS.EDITOR.REMOVE_AREA' | translate" (click)="removeArea(a)">
                <i class="pi pi-trash" aria-hidden="true"></i>
              </button>
            </div>
          }
        </div>

        @if (openArea === a) {
          <ol class="fvr-area__items">
            @for (item of section.items; track $index; let i = $index) {
              <li>
                @if (isEditing(a, i) && editing; as edit) {
                  <div class="fvr-cp-edit">
                    <div class="fvr-cp-edit__title">
                      <span class="fvr-cp__num">{{ i + 1 }}</span>
                      <div class="fvr-field fvr-cp-edit__title-field">
                        <label [for]="'fvr-cp-title-' + a + '-' + i">{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.CHECKPOINT' | translate }} <span class="fvr-required">*</span></label>
                        <input pInputText [id]="'fvr-cp-title-' + a + '-' + i" type="text" maxlength="300" [(ngModel)]="edit.title" />
                      </div>
                      <button type="button" class="fas-icon-btn" [disabled]="i === 0" [attr.aria-label]="'FLEXI_SAFETY_ROUNDS.EDITOR.MOVE_UP' | translate" (click)="moveEditedItem(-1)">
                        <i class="pi pi-arrow-up" aria-hidden="true"></i>
                      </button>
                      <button type="button" class="fas-icon-btn" [disabled]="i === section.items.length - 1" [attr.aria-label]="'FLEXI_SAFETY_ROUNDS.EDITOR.MOVE_DOWN' | translate" (click)="moveEditedItem(1)">
                        <i class="pi pi-arrow-down" aria-hidden="true"></i>
                      </button>
                    </div>
                    <div class="fvr-editor__grid">
                      <div class="fvr-field">
                        <label [for]="'fvr-cp-guide-' + a + '-' + i">{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.GUIDANCE' | translate }}</label>
                        <textarea pTextarea [id]="'fvr-cp-guide-' + a + '-' + i" rows="3" maxlength="2000" [(ngModel)]="edit.guidance"></textarea>
                      </div>
                      <div class="fvr-field">
                        <label [for]="'fvr-cp-std-' + a + '-' + i">
                          {{ 'FLEXI_SAFETY_ROUNDS.EDITOR.STANDARD_TEXTS' | translate }}
                          <span class="fas-muted">{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.ONE_PER_LINE' | translate }}</span>
                        </label>
                        <textarea pTextarea [id]="'fvr-cp-std-' + a + '-' + i" rows="3" maxlength="4000" [(ngModel)]="edit.standardTexts"></textarea>
                      </div>
                    </div>
                    <div class="fvr-field">
                      <span class="fvr-label">{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.REGULATIONS' | translate }}</span>
                      @for (ref of edit.references; track $index; let r = $index) {
                        <div class="fvr-cp-edit__ref">
                          <input
                            pInputText
                            type="text"
                            maxlength="300"
                            [(ngModel)]="ref.Title"
                            [attr.aria-label]="'FLEXI_SAFETY_ROUNDS.EDITOR.REGULATION_TITLE' | translate: { index: r + 1 }"
                            [placeholder]="'FLEXI_SAFETY_ROUNDS.EDITOR.REGULATION_TITLE_PLACEHOLDER' | translate"
                          />
                          <input
                            pInputText
                            type="url"
                            maxlength="1000"
                            [(ngModel)]="ref.Url"
                            [attr.aria-label]="'FLEXI_SAFETY_ROUNDS.EDITOR.REGULATION_URL' | translate: { index: r + 1 }"
                            placeholder="https://lovdata.no/..."
                          />
                          <button type="button" class="fas-icon-btn" [attr.aria-label]="'FLEXI_SAFETY_ROUNDS.EDITOR.REMOVE_REGULATION' | translate" (click)="removeReference(r)">
                            <i class="pi pi-times" aria-hidden="true"></i>
                          </button>
                        </div>
                      }
                      <div>
                        <button type="button" class="fas-btn fas-btn--text" (click)="addReference()">
                          <i class="pi pi-plus" aria-hidden="true"></i>{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.ADD_REGULATION' | translate }}
                        </button>
                      </div>
                    </div>
                    <div class="fvr-cp-edit__foot">
                      <div class="fvr-cp-edit__photos">
                        <label class="fvr-check">
                          <input type="checkbox" [(ngModel)]="edit.photoAt4" />
                          <span>{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.PHOTO_AT' | translate: { grade: 4 } }}</span>
                        </label>
                        <label class="fvr-check">
                          <input type="checkbox" [(ngModel)]="edit.photoAt3" />
                          <span>{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.PHOTO_AT' | translate: { grade: 3 } }}</span>
                        </label>
                      </div>
                      <div class="fvr-cp-edit__buttons">
                        <button type="button" class="fas-btn fas-btn--text fvr-danger" (click)="removeEditedItem()">{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.DELETE' | translate }}</button>
                        <button type="button" class="fas-btn fas-btn--ghost" (click)="cancelEdit()">{{ 'CANCEL' | translate }}</button>
                        <button type="button" class="fas-btn fas-btn--primary" [disabled]="!edit.title.trim()" (click)="applyEdit()">
                          {{ 'FLEXI_SAFETY_ROUNDS.EDITOR.SAVE_CHECKPOINT' | translate }}
                        </button>
                      </div>
                    </div>
                  </div>
                } @else {
                  <div class="fvr-cp">
                    <span class="fvr-cp__num">{{ i + 1 }}</span>
                    <div class="fvr-cp__text">
                      <strong>{{ item.Title }}</strong>
                      @if (itemMeta(item); as meta) {
                        <span class="fas-muted">{{ meta }}</span>
                      }
                    </div>
                    @if (!isArchived) {
                      <button type="button" class="fas-btn fas-btn--text" [disabled]="!!editing" (click)="editItem(a, i)">
                        {{ 'FLEXI_SAFETY_ROUNDS.EDITOR.EDIT' | translate }}
                      </button>
                    }
                  </div>
                }
              </li>
            } @empty {
              <li class="fas-muted">{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.AREA_EMPTY' | translate }}</li>
            }
          </ol>
          @if (!isArchived) {
            <div>
              <button type="button" class="fas-btn fas-btn--text" [disabled]="!!editing" (click)="addItem(a)">
                <i class="pi pi-plus" aria-hidden="true"></i>{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.ADD_CHECKPOINT' | translate }}
              </button>
            </div>
          }
        }
      </section>
    }

    @if (!isArchived) {
      <div>
        <button type="button" class="fas-btn fas-btn--ghost" [disabled]="!!editing" (click)="addArea()">
          <i class="pi pi-plus" aria-hidden="true"></i>{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.ADD_AREA' | translate }}
        </button>
      </div>
    }

    <!-- ═══════════ Assigned businesses ═══════════ -->
    <section class="fvr-card fvr-editor__card" aria-labelledby="fvr-assigned-heading">
      <div class="fvr-head">
        <div>
          <h3 class="fvr-h3" id="fvr-assigned-heading">{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.ASSIGNED' | translate }}</h3>
          <span class="fas-muted" *ngIf="!assignmentsLoading">
            {{ 'FLEXI_SAFETY_ROUNDS.EDITOR.ASSIGNED_SUMMARY' | translate: { count: assignments.length, defaults: assignedDefaultCount } }}
          </span>
        </div>
        <button
          type="button"
          class="fas-btn fas-btn--ghost"
          [disabled]="!isPublished"
          [pTooltip]="!isPublished ? ('FLEXI_SAFETY_ROUNDS.EDITOR.ASSIGN_NEEDS_PUBLISHED' | translate) : ''"
          (click)="openAssign()"
        >
          {{ 'FLEXI_SAFETY_ROUNDS.EDITOR.PICK_BUSINESSES' | translate }}
        </button>
      </div>
      @if (assignmentsLoading) { <flexi-safety-round-skeleton /> }
      <div class="fvr-table-card" *ngIf="!assignmentsLoading">
        <p-table
          [value]="assignments"
          dataKey="BusinessId"
          [loading]="assignmentsLoading"
          [rows]="10"
          [paginator]="assignments.length > 10"
          styleClass="p-datatable-sm"
        >
          <ng-template pTemplate="header">
            <tr>
              <th>{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.COLUMN_BUSINESS' | translate }}</th>
              <th>{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.COLUMN_ASSIGNED_ON' | translate }}</th>
              <th>{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.COLUMN_DEFAULT' | translate }}</th>
              <th class="fvr-editor__action-col"><span class="fas-sr-only">{{ 'ACTIONS' | translate }}</span></th>
            </tr>
          </ng-template>
          <ng-template pTemplate="body" let-row>
            <tr>
              <td>{{ row.BusinessName }}</td>
              <td class="fvr-num">{{ row.AssignedOn | date: 'dd-MM-yyyy' }}</td>
              <td>{{ (row.IsDefault ? 'FLEXI_SAFETY_ROUNDS.YES' : 'FLEXI_SAFETY_ROUNDS.NO') | translate }}</td>
              <td class="fvr-editor__action-col">
                <button
                  type="button"
                  class="fas-btn fas-btn--text"
                  [disabled]="removingIds.has(row.BusinessId)"
                  [attr.aria-label]="'FLEXI_SAFETY_ROUNDS.EDITOR.REMOVE_ARIA' | translate: { name: row.BusinessName }"
                  (click)="confirmUnassign(row)"
                >
                  {{ (removingIds.has(row.BusinessId) ? 'FLEXI_SAFETY_ROUNDS.FEEDBACK.UPDATING' : 'FLEXI_SAFETY_ROUNDS.EDITOR.REMOVE') | translate }}
                </button>
              </td>
            </tr>
          </ng-template>
          <ng-template pTemplate="emptymessage">
            <tr>
              <td colspan="4" class="fas-muted">{{ 'FLEXI_SAFETY_ROUNDS.EDITOR.NO_ASSIGNMENTS' | translate }}</td>
            </tr>
          </ng-template>
        </p-table>
      </div>
    </section>
  }
  </div>
</div>

@if (assignOpen && template) {
  <flexi-safety-round-assign-drawer
    [templateId]="template.Id"
    [templateName]="template.Name"
    (closed)="assignClosed($event)"
  ></flexi-safety-round-assign-drawer>
}

<p-confirmDialog key="fvrEditor" [style]="{ width: '480px', maxWidth: '95vw' }"></p-confirmDialog>
